import React, { useMemo, useState } from 'react';
import { Link as RouterLink } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Container,
  Divider,
  FormControl,
  FormControlLabel,
  FormHelperText,
  Grid,
  InputAdornment,
  Link,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { HomeRounded, SavingsRounded } from '@mui/icons-material';
import {
  monthsToYearsMonths,
  calculateBasePayment,
  calculateWithOverpayment,
} from './mortgageOverpaymentUtils';

const formatCurrency = (value) =>
  new Intl.NumberFormat('en-GB', {
    style: 'currency',
    currency: 'GBP',
    minimumFractionDigits: 2,
  }).format(Number.isFinite(value) ? value : 0);

const formatNumber = (value) => (Number.isFinite(value) ? value.toLocaleString('en-GB') : '');

const parseNumberInput = (value) => {
  const sanitized = typeof value === 'string' ? value.replace(/,/g, '') : value;
  const parsed = parseFloat(sanitized);
  return Number.isFinite(parsed) ? parsed : 0;
};

const MortgageOverpaymentCalculatorApp = () => {
  const [balance, setBalance] = useState(250000);
  const [interestRate, setInterestRate] = useState(3.5);
  const [termYears, setTermYears] = useState(22);
  const [monthlyOverpayment, setMonthlyOverpayment] = useState(200);
  const [savingsRate, setSavingsRate] = useState(4);
  const [savingsGrowth, setSavingsGrowth] = useState('compound'); // 'compound' | 'simple'

  const termMonths = Math.max(Math.floor(termYears * 12), 0);
  const savingsMonths = Math.max(Math.round(termYears * 12), 0);
  const principal = Math.max(balance, 0);

  const { monthlyPayment } = useMemo(
    () => calculateBasePayment(principal, interestRate, termMonths),
    [principal, interestRate, termMonths]
  );

  const {
    months: baselineMonths,
    totalInterest: baselineInterest,
  } = useMemo(
    () =>
      calculateWithOverpayment(
        principal,
        interestRate,
        monthlyPayment,
        0,
        termMonths
      ),
    [principal, interestRate, monthlyPayment, termMonths]
  );

  const { months: overpayMonths, totalInterest: overpayInterest, totalPaid: overpayPaid } = useMemo(
    () =>
      calculateWithOverpayment(
        principal,
        interestRate,
        monthlyPayment,
        Math.max(monthlyOverpayment, 0),
        termMonths
      ),
    [principal, interestRate, monthlyPayment, monthlyOverpayment, termMonths]
  );

  const interestSaved = Math.max(baselineInterest - overpayInterest, 0);
  const monthsSaved = Math.max(baselineMonths - overpayMonths, 0);

  const payoffSummary = monthsToYearsMonths(overpayMonths);
  const baselineTermSummary = monthsToYearsMonths(baselineMonths);
  const savingsTermSummary = monthsToYearsMonths(savingsMonths);
  const monthlyTotalWithOverpay = monthlyPayment + Math.max(monthlyOverpayment, 0);
  const monthlyOverpaymentDisplay = formatCurrency(Math.max(monthlyOverpayment, 0));
  const { savingsBalance, savingsInterestEarned } = useMemo(() => {
    const monthlyRate = Math.pow(1 + savingsRate / 100, 1 / 12) - 1;
    const contributions = Math.max(monthlyOverpayment, 0);
    const n = savingsMonths;

    if (contributions === 0 || n === 0) return { savingsBalance: 0, savingsInterestEarned: 0 };

    const totalContrib = contributions * n;
    const years = n / 12;

    if (savingsGrowth === 'simple') {
      let principalOnly = 0;
      let interestAccrued = 0;

      // Add contributions monthly; apply simple interest on current principal without compounding.
      for (let i = 0; i < n; i += 1) {
        principalOnly += contributions;
        interestAccrued += principalOnly * monthlyRate;
      }

      const interest = Math.max(interestAccrued, 0);
      return {
        savingsBalance: principalOnly + interest,
        savingsInterestEarned: interest,
      };
    }

    if (monthlyRate === 0) {
      return { savingsBalance: totalContrib, savingsInterestEarned: 0 };
    }

    // Compound monthly using AER-derived monthly rate; contributions applied end-of-month.
    let balance = 0;
    for (let i = 0; i < n; i += 1) {
      balance += balance * monthlyRate;
      balance += contributions;
    }

    const interestEarned = Math.max(balance - totalContrib, 0);
    return { savingsBalance: balance, savingsInterestEarned: interestEarned };
  }, [monthlyOverpayment, savingsRate, savingsMonths, savingsGrowth]);
  const overpayVsSaveDelta = interestSaved - savingsInterestEarned;
  const favorsOverpaying = overpayVsSaveDelta >= 0;
  const savingsDecisionText = favorsOverpaying
    ? 'Overpaying saves more interest than saving this amount.'
    : 'Saving this amount earns more than the interest you save by overpaying.';
  const savingsGrowthLabel = savingsGrowth === 'simple' ? 'simple interest' : 'compounding monthly';
  const savingsNarrative = `If you put ${monthlyOverpaymentDisplay} per month into a ${savingsRate}% account (${savingsGrowthLabel}) for ${savingsTermSummary}, you would end with ${formatCurrency(savingsBalance)} — ${formatCurrency(savingsInterestEarned)} of that is interest. Compared to overpaying, you'd ${
    favorsOverpaying ? `save ${formatCurrency(overpayVsSaveDelta)} more in mortgage interest` : `earn ${formatCurrency(Math.abs(overpayVsSaveDelta))} more in savings interest`
  }.`;
  const highlightStyles = (isActive) => ({
    px: 1.25,
    py: 0.9,
    borderRadius: 2,
    border: '1px solid',
    borderColor: isActive ? 'primary.main' : 'divider',
    bgcolor: isActive ? 'primary.main' : 'action.hover',
    color: isActive ? 'primary.contrastText' : 'text.secondary',
    minWidth: 108,
    textAlign: 'center',
    transition: 'all 0.2s ease',
  });

  return (
    <>
      <Helmet>
        <title>Mortgage Overpayment Calculator | Talk About Finance</title>
        <meta
          name="description"
          content="See how monthly overpayments cut your mortgage term and save interest. Adjust numbers in real time to find the best overpayment plan."
        />
        <link rel="canonical" href="https://talkabout.finance/apps/mortgage-overpayment-calculator" />
      </Helmet>

      <Container sx={{ mt: 6, mb: 8, pb: 6 }}>
        <Paper
          elevation={0}
          sx={{
            p: { xs: 3, md: 4 },
            mb: 4,
            borderRadius: 3,
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: (theme) => theme.shadows[1],
            background: (theme) =>
              theme.palette.mode === 'light'
                ? 'linear-gradient(135deg, #f7f9fc 0%, #ffffff 45%, #f7f9fc 100%)'
                : 'linear-gradient(135deg, #1d1f24 0%, #17181c 45%, #1d1f24 100%)',
          }}
        >
          <Stack spacing={1.5} sx={{ flex: 1, minWidth: 280, width: '100%' }}>
            <Box>
              <Button variant="outlined" color="primary" component={RouterLink} to="/apps">
                Back to apps
              </Button>
            </Box>
            <Typography variant="overline" color="text.secondary" sx={{ letterSpacing: 1 }}>
              Calculator
            </Typography>
            <Typography variant="h4" sx={{ fontWeight: 700 }}>
              Mortgage Overpayment
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ maxWidth: '100%' }}>
              Add monthly overpayments and instantly see how much interest you save and how many months you shave off your mortgage.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Want to compare instead saving that money? Scroll down to the optional “Overpayment vs money saved” section to see which option wins for you.
            </Typography>
          </Stack>
        </Paper>

        <Paper elevation={3} sx={{ p: { xs: 3, md: 4 } }}>
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
              <TextField
                label="Remaining Mortgage Balance"
                variant="outlined"
                fullWidth
                type="text"
                value={formatNumber(balance)}
                onChange={(e) => setBalance(Math.max(parseNumberInput(e.target.value), 0))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  inputMode: 'decimal',
                }}
                helperText="Use the current outstanding balance, not property price."
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Interest Rate (%)"
                variant="outlined"
                fullWidth
                type="number"
                value={interestRate}
                onChange={(e) => {
                  const next = parseNumberInput(e.target.value);
                  setInterestRate(Math.max(next, 0));
                }}
                InputProps={{ inputProps: { step: 0.1, min: 0 } }}
              />
            </Grid>
            <Grid item xs={12} sm={3}>
              <TextField
                label="Remaining Term (years)"
                variant="outlined"
                fullWidth
                type="number"
                value={termYears}
                onChange={(e) => {
                  const next = parseNumberInput(e.target.value);
                  setTermYears(Math.max(next, 0));
                }}
                InputProps={{ inputProps: { step: 0.5, min: 1 } }}
              />
            </Grid>

            <Grid item xs={12} sm={4}>
              <TextField
                label="Monthly Overpayment"
                variant="outlined"
                fullWidth
                type="text"
                value={formatNumber(monthlyOverpayment)}
                onChange={(e) => setMonthlyOverpayment(Math.max(parseNumberInput(e.target.value), 0))}
                InputProps={{
                  startAdornment: <InputAdornment position="start">£</InputAdornment>,
                  inputMode: 'decimal',
                }}
                helperText="Added on top of your normal monthly payment."
              />
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                Without overpayments
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Monthly payment</Typography>
                <Typography fontWeight="bold">{formatCurrency(monthlyPayment)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Total interest over term</Typography>
                <Typography>{formatCurrency(baselineInterest)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Time to repay</Typography>
                <Typography>{baselineTermSummary}</Typography>
              </Box>
            </Grid>

            <Grid item xs={12} md={6}>
              <Typography variant="h6" gutterBottom>
                With your overpayment
              </Typography>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Monthly (incl. overpayment)</Typography>
                <Typography fontWeight="bold">{formatCurrency(monthlyTotalWithOverpay)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography color="text.secondary">Total interest</Typography>
                <Typography>{formatCurrency(overpayInterest)}</Typography>
              </Box>
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography color="text.secondary">Time to repay</Typography>
                <Typography>{payoffSummary}</Typography>
              </Box>
            </Grid>
          </Grid>

          <Divider sx={{ my: 3 }} />

          <Grid container spacing={3}>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Stack spacing={0.5}>
                  <Typography color="text.secondary">Interest saved</Typography>
                  <Typography variant="h6">{formatCurrency(interestSaved)}</Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Stack spacing={0.5}>
                  <Typography color="text.secondary">Term reduced by</Typography>
                  <Typography variant="h6">{monthsToYearsMonths(monthsSaved)}</Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2, height: '100%' }}>
                <Stack spacing={0.5}>
                  <Typography color="text.secondary">Total paid with overpayment</Typography>
                  <Typography variant="h6">{formatCurrency(overpayPaid)}</Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>

          <Box sx={{ mt: 4, pt: 2 }}>
            <Alert severity="info">
              Check your lender&apos;s overpayment rules and penalties. This tool assumes fixed-rate, level monthly payments and no overpayment fees.
            </Alert>
          </Box>
        </Paper>

        <Paper elevation={1} sx={{ p: { xs: 3, md: 4 }, mt: 3 }}>
          <Typography variant="h6" gutterBottom>
            Overpayment vs money saved
          </Typography>
          <Typography variant="subtitle1" gutterBottom>
            Savings vs overpaying
          </Typography>
          <Grid container spacing={2} sx={{ mb: 1 }}>
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                label="Savings interest if not overpaying (%)"
                variant="outlined"
                fullWidth
                type="number"
                value={savingsRate}
                onChange={(e) => {
                  const next = parseNumberInput(e.target.value);
                  setSavingsRate(Math.max(next, 0));
                }}
                InputProps={{ inputProps: { step: 0.1, min: 0 } }}
                helperText="Assumed savings account rate for that overpayment amount."
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <FormControl fullWidth>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={savingsGrowth === 'compound'}
                      onChange={(e) => setSavingsGrowth(e.target.checked ? 'compound' : 'simple')}
                    />
                  }
                  label="Compound interest (monthly)"
                />
                <FormHelperText>
                  Tick to treat savings as compounding monthly using the AER-derived monthly rate. Want to dig deeper?{' '}
                  <Link component={RouterLink} to="/apps/compound-interest-calculator">
                    Open the compound interest calculator
                  </Link>
                  .
                </FormHelperText>
              </FormControl>
            </Grid>
          </Grid>

          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={4}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Savings balance after {savingsTermSummary}
                  </Typography>
                  <Typography variant="h6">{formatCurrency(savingsBalance)}</Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={0.5}>
                  <Typography variant="body2" color="text.secondary">
                    Interest earned in savings
                  </Typography>
                  <Typography variant="h6">{formatCurrency(savingsInterestEarned)}</Typography>
                </Stack>
              </Paper>
            </Grid>
            <Grid item xs={12} md={4}>
              <Paper variant="outlined" sx={{ p: 2 }}>
                <Stack spacing={1}>
                  <Typography variant="body2" color="text.secondary">
                    Difference vs overpaying (interest)
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={1.5}>
                    <Stack alignItems="center" spacing={0.5} sx={highlightStyles(favorsOverpaying)}>
                      <HomeRounded fontSize="small" />
                      <Typography variant="caption" sx={{ color: 'inherit' }}>
                        Overpaying
                      </Typography>
                    </Stack>
                    <Typography variant="caption" color="text.secondary">
                      vs
                    </Typography>
                    <Stack alignItems="center" spacing={0.5} sx={highlightStyles(!favorsOverpaying)}>
                      <SavingsRounded fontSize="small" />
                      <Typography variant="caption" sx={{ color: 'inherit' }}>
                        Saving
                      </Typography>
                    </Stack>
                  </Stack>
                  <Typography variant="h6" color="text.primary">
                    {formatCurrency(overpayVsSaveDelta)}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    {savingsDecisionText}
                  </Typography>
                </Stack>
              </Paper>
            </Grid>
          </Grid>
          <Typography variant="body2" sx={{ mt: 2 }}>
            {savingsNarrative}
          </Typography>
        </Paper>
        <Box sx={{ mt: 3 }}>
          <Typography variant="subtitle2" gutterBottom>
            About these calculations
          </Typography>
          <Typography variant="body2" color="text.secondary">
            These figures are illustrative and based on the assumptions shown. They may not reflect the terms of specific financial products or your individual circumstances. You should verify results independently and, where appropriate, seek advice from a qualified professional.
          </Typography>
        </Box>
      </Container>
    </>
  );
};

export default MortgageOverpaymentCalculatorApp;
